import React, { useEffect, useState, useCallback, useMemo } from 'react';
import ChartTooltip from '../components/ChartTooltip';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import { getEvents, parseReport } from '../api';
import ChartCard from '../components/ChartCard';

const COLORS = ['#38bdf8','#818cf8','#34d399','#f472b6','#fb923c','#fbbf24','#a78bfa','#f87171','#22d3ee','#4ade80'];

export default function Events({ propertyId, startDate, endDate }) {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [sortBy,  setSortBy]  = useState('eventCount');
  const [sortDir, setSortDir] = useState('desc');
  const [tab,     setTab]     = useState('chart'); // chart | table

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res  = await getEvents(propertyId, startDate, endDate);
      const rows = parseReport(res.data).sort((a, b) => b.eventCount - a.eventCount);
      setEvents(rows);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [propertyId, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const totalEvents = useMemo(() => events.reduce((s, e) => s + (e.eventCount || 0), 0), [events]);

  // filtered + sorted
  const filtered = useMemo(() => {
    let rows = events.filter(e =>
      !search || e.eventName?.toLowerCase().includes(search.toLowerCase())
    );
    rows = [...rows].sort((a, b) => {
      const av = a[sortBy] || 0, bv = b[sortBy] || 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return rows;
  }, [events, search, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const sortIcon = (col) => sortBy === col ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ' ↕';

  if (loading) return <Loader />;

  const chartData = filtered.slice(0, 15);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:18 }}>

      {/* ── Top KPI row ───────────────────────── */}
      <div style={styles.kpiRow}>
        <div style={styles.kpi}>
          <div style={styles.kpiIcon}>⚡</div>
          <div>
            <div style={styles.kpiVal}>{totalEvents.toLocaleString()}</div>
            <div style={styles.kpiLbl}>Total Events</div>
          </div>
        </div>
        <div style={styles.kpi}>
          <div style={{ ...styles.kpiIcon, background:'rgba(129,140,248,0.15)', color:'#818cf8' }}>📋</div>
          <div>
            <div style={{ ...styles.kpiVal, color:'#818cf8' }}>{events.length}</div>
            <div style={styles.kpiLbl}>Unique Types</div>
          </div>
        </div>
        <div style={styles.kpi}>
          <div style={{ ...styles.kpiIcon, background:'rgba(52,211,153,0.15)', color:'#34d399' }}>👤</div>
          <div>
            <div style={{ ...styles.kpiVal, color:'#34d399' }}>
              {events.length > 0 ? (events.reduce((s,e)=>s+(e.eventCountPerUser||0),0)/events.length).toFixed(1) : '0'}
            </div>
            <div style={styles.kpiLbl}>Avg Events/User</div>
          </div>
        </div>
        <div style={styles.kpi}>
          <div style={{ ...styles.kpiIcon, background:'rgba(244,114,182,0.15)', color:'#f472b6' }}>🏆</div>
          <div>
            <div style={{ ...styles.kpiVal, color:'#f472b6' }}>{events[0]?.eventName || '—'}</div>
            <div style={styles.kpiLbl}>Top Event</div>
          </div>
        </div>
      </div>

      {/* ── Search + Filter Bar ───────────────── */}
      <div style={styles.filterBar}>
        <div style={styles.searchWrap}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            style={styles.searchInput}
            placeholder="Search events by name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button style={styles.clearBtn} onClick={() => setSearch('')}>✕</button>
          )}
        </div>
        <div style={styles.resultCount}>
          {filtered.length} of {events.length} events
          {search && <span style={{ color:'var(--accent)', marginLeft:6 }}>matching "{search}"</span>}
        </div>
        <div style={styles.tabToggle}>
          {['chart','table'].map(t => (
            <button key={t} style={{ ...styles.tabBtn, ...(tab===t?styles.tabBtnActive:{}) }} onClick={() => setTab(t)}>
              {t === 'chart' ? '📊 Chart' : '📋 Table'}
            </button>
          ))}
        </div>
      </div>

      {/* ── Chart View ───────────────────────── */}
      {tab === 'chart' && (
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16 }}>
          <ChartCard title="Events by Count" subtitle={`Top ${Math.min(chartData.length,15)} ${search ? 'filtered' : ''} events`}>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={chartData} layout="vertical" margin={{ top:0, right:20, left:130, bottom:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number" tick={{ fill:'var(--text3)', fontSize:10 }} />
                <YAxis type="category" dataKey="eventName" tick={{ fill:'var(--text2)', fontSize:11 }} width={125} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="eventCount" name="Count" radius={[0,4,4,0]}>
                  {chartData.map((_,i) => <Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Events/User Depth" subtitle="Engagement per user">
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={chartData.slice(0,10)} layout="vertical" margin={{ top:0, right:20, left:130, bottom:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number" tick={{ fill:'var(--text3)', fontSize:10 }} />
                <YAxis type="category" dataKey="eventName" tick={{ fill:'var(--text2)', fontSize:10 }} width={125} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="eventCountPerUser" name="Events/User" radius={[0,4,4,0]}>
                  {chartData.slice(0,10).map((_,i) => <Cell key={i} fill={COLORS[(i+3)%COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* ── Table View ───────────────────────── */}
      {tab === 'table' && (
        <div style={styles.tableWrap}>
          <div style={styles.thead}>
            {[
              { key:'#',                label:'#',           w:'40px' },
              { key:'eventName',        label:'Event Name',  w:'2fr' },
              { key:'eventCount',       label:'Count',       w:'1fr' },
              { key:'eventCountPerUser',label:'Per User',    w:'1fr' },
              { key:'pct',              label:'% of Total',  w:'1fr' },
            ].map(h => (
              <span key={h.key} style={{ cursor: h.key==='#'?'default':'pointer', userSelect:'none' }}
                onClick={() => h.key !== '#' && h.key !== 'pct' && toggleSort(h.key)}>
                {h.label}{h.key!=='#' && h.key!=='pct' ? sortIcon(h.key) : ''}
              </span>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding:'30px', textAlign:'center', color:'var(--text3)', fontSize:13 }}>
              No events match "{search}"
            </div>
          ) : filtered.map((r, i) => (
            <div key={i} style={{ ...styles.trow, background: i%2===0 ? 'transparent' : 'rgba(99,179,237,0.02)' }}>
              <span style={{ color:'var(--text3)', fontFamily:'Space Mono,monospace', fontSize:11 }}>{i+1}</span>
              <span style={{ color:COLORS[i%COLORS.length], fontWeight:600 }}>{r.eventName}</span>
              <span style={{ fontFamily:'Space Mono,monospace', fontWeight:600 }}>{r.eventCount?.toLocaleString()}</span>
              <span style={{ fontFamily:'Space Mono,monospace' }}>{r.eventCountPerUser?.toFixed(2)}</span>
              <span>
                <div style={styles.pctBarWrap}>
                  <div style={{ ...styles.pctBar, width:`${totalEvents>0?(r.eventCount/totalEvents*100):0}%`, background:COLORS[i%COLORS.length] }} />
                  <span style={{ fontSize:10, color:'var(--text3)', marginLeft:6 }}>
                    {totalEvents>0?((r.eventCount/totalEvents)*100).toFixed(1):0}%
                  </span>
                </div>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Share pie summary ─────────────────── */}
      {tab === 'chart' && filtered.length > 0 && (
        <ChartCard title="Event Distribution Summary" subtitle="Top 5 events share of total">
          <div style={{ display:'flex', flexDirection:'column', gap:8, padding:'4px 0' }}>
            {filtered.slice(0,8).map((e,i) => {
              const pct = totalEvents > 0 ? (e.eventCount/totalEvents*100) : 0;
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:120, fontSize:11, color:COLORS[i%COLORS.length], fontWeight:600, flexShrink:0 }}>{e.eventName}</div>
                  <div style={{ flex:1, background:'var(--surface2)', borderRadius:4, height:8, overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:COLORS[i%COLORS.length], borderRadius:4, transition:'width 0.5s' }} />
                  </div>
                  <div style={{ width:50, fontSize:11, color:'var(--text3)', textAlign:'right', fontFamily:'Space Mono,monospace' }}>{pct.toFixed(1)}%</div>
                  <div style={{ width:70, fontSize:11, color:'var(--text2)', textAlign:'right', fontFamily:'Space Mono,monospace' }}>{e.eventCount?.toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        </ChartCard>
      )}
    </div>
  );
}

function Loader() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300, flexDirection:'column', gap:12 }}>
      <div style={{ fontSize:36, color:'var(--accent)', animation:'spin 1s linear infinite' }}>◈</div>
      <div style={{ color:'var(--text3)', fontSize:13 }}>Loading events…</div>
    </div>
  );
}

const styles = {
  kpiRow: { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 },
  kpi: {
    background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14,
    padding:'16px 18px', display:'flex', alignItems:'center', gap:14,
    boxShadow:'var(--shadow-sm)',
  },
  kpiIcon: { width:42, height:42, borderRadius:11, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, background:'rgba(56,189,248,0.15)', color:'#38bdf8', flexShrink:0 },
  kpiVal:  { fontSize:20, fontWeight:800, color:'var(--text)', letterSpacing:'-0.5px' },
  kpiLbl:  { fontSize:11, color:'var(--text3)', marginTop:3, fontWeight:500 },

  filterBar: {
    background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12,
    padding:'12px 16px', display:'flex', alignItems:'center', gap:14, flexWrap:'wrap',
  },
  searchWrap: {
    display:'flex', alignItems:'center', gap:8,
    background:'var(--surface2)', border:'1px solid var(--border)',
    borderRadius:9, padding:'8px 12px', flex:1, minWidth:220,
  },
  searchIcon:  { fontSize:13, flexShrink:0, color:'var(--text3)' },
  searchInput: { flex:1, background:'none', border:'none', outline:'none', color:'var(--text)', fontSize:13, fontFamily:'Sora,sans-serif' },
  clearBtn:    { background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:12, padding:0, flexShrink:0 },
  resultCount: { fontSize:12, color:'var(--text3)', flexShrink:0 },
  tabToggle:   { display:'flex', gap:6, marginLeft:'auto' },
  tabBtn:      { padding:'7px 14px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text2)', fontSize:12, fontWeight:500, cursor:'pointer' },
  tabBtnActive:{ background:'rgba(56,189,248,0.15)', borderColor:'var(--accent)', color:'var(--accent)' },

  tableWrap: { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden' },
  thead: {
    display:'grid', gridTemplateColumns:'40px 2fr 1fr 1fr 1.5fr',
    padding:'10px 16px', fontSize:10, color:'var(--text3)',
    fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase',
    background:'var(--surface2)', borderBottom:'1px solid var(--border)', gap:8,
  },
  trow: {
    display:'grid', gridTemplateColumns:'40px 2fr 1fr 1fr 1.5fr',
    padding:'11px 16px', fontSize:12, color:'var(--text2)',
    borderBottom:'1px solid var(--border2)', gap:8, alignItems:'center',
    transition:'background 0.1s',
  },
  pctBarWrap: { display:'flex', alignItems:'center' },
  pctBar:     { height:6, borderRadius:3, minWidth:2, transition:'width 0.5s' },
};
